/**
 * Freedom Pay Integration Service
 *
 * Документация: https://freedompay.kz/docs-en/gateway-api/intro
 *
 * Тестовый режим: https://test-api.freedompay.kz
 * Production: https://api.freedompay.kz
 *
 * ВАЖНО: Генерация подписей (pg_sig) должна происходить на backend (Firebase Functions)
 * т.к. требует secret_key который не должен быть на клиенте!
 */

// Конфигурация Freedom Pay (берется из .env)
const FREEDOM_PAY_CONFIG = {
    // Merchant ID будет получаться от Firebase Functions
    widgetKey: process.env.REACT_APP_FREEDOM_PAY_WIDGET_KEY,
    apiUrl: process.env.REACT_APP_FREEDOM_PAY_API_URL || 'https://api.freedompay.kz',
    testMode: process.env.NODE_ENV === 'development',
};

/**
 * Генерация подписи для Freedom Pay запроса
 *
 * ⚠️ ВАЖНО: Эта функция должна вызываться ТОЛЬКО на backend (Firebase Functions)
 * т.к. требует secret_key который не должен быть доступен на клиенте!
 *
 * Алгоритм:
 * 1. Собираем все параметры в алфавитном порядке
 * 2. Добавляем pg_salt (случайная строка)
 * 3. Формируем строку: script_name;param1;param2;...;pg_salt;secret_key
 * 4. Берём MD5 от этой строки
 *
 * Реализация для Firebase Functions (Node.js):
 * const crypto = require('crypto');
 * const md5 = crypto.createHash('md5').update(signatureString).digest('hex');
 *
 * @param {Object} params - Параметры запроса
 * @param {string} secretKey - Секретный ключ
 * @param {string} scriptName - Имя скрипта (например, 'payment.php')
 * @returns {string} MD5 подпись
 */
export const generateSignature = (params, secretKey, scriptName = 'payment.php') => {
    throw new Error('generateSignature должен вызываться только на backend! Используйте Firebase Functions.');
};

/**
 * Генерация случайной соли
 * @returns {string}
 */
export const generateSalt = () => {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
};

/**
 * Генерация уникального order_id
 * @returns {string}
 */
export const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `ORDER-${timestamp}-${random}`;
};

/**
 * Инициализация Freedom Pay платежа через Firebase Function
 *
 * @param {Object} options - Параметры платежа
 * @param {number} options.amount - Сумма платежа (в тенге)
 * @param {string} options.orderId - ID заказа
 * @param {string} options.description - Описание платежа
 * @param {string} options.customerEmail - Email клиента
 * @param {string} options.customerPhone - Телефон клиента
 * @param {string} options.customerName - Имя клиента
 * @param {string} options.accountId - ID аккаунта пользователя
 * @param {Function} options.onSuccess - Callback при успешной оплате (не используется при redirect)
 * @param {Function} options.onError - Callback при ошибке
 * @returns {Promise}
 */
export const initFreedomPayWidget = async (options) => {
    const {
        amount,
        orderId,
        description,
        customerEmail,
        customerPhone,
        customerName,
        accountId,
        onError
    } = options;

    try {
        console.log('💳 Creating Freedom Pay payment via Firebase Function...', {
            amount,
            orderId,
            customerEmail
        });

        // Вызываем Firebase Function для создания платежа
        const response = await fetch('https://us-central1-eparcel-kz.cloudfunctions.net/createFreedomPayPayment', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                orderId,
                amount,
                currency: 'KZT',
                description: `${description} | userId:${accountId}`, // Встраиваем userId в description для callback
                customerEmail,
                customerPhone,
                customerName,
                userId: accountId
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create payment');
        }

        const result = await response.json();

        console.log('✅ Freedom Pay payment created:', result);

        // Проверяем что получили redirect URL
        if (!result.redirectUrl) {
            throw new Error('No redirect URL received from Freedom Pay');
        }

        // Редиректим на платежную страницу Freedom Pay
        console.log('🔄 Redirecting to Freedom Pay payment page...');
        window.location.href = result.redirectUrl;

        return { success: true };
    } catch (error) {
        console.error('❌ Freedom Pay payment initialization error:', error);
        if (onError) onError(error);
        throw error;
    }
};

/**
 * Создание платежа через API (для backend)
 *
 * Это для Firebase Functions, не для frontend!
 *
 * @param {Object} paymentData - Данные платежа
 * @returns {Promise}
 */
export const createPayment = async (paymentData) => {
    const {
        merchantId,
        amount,
        orderId,
        description,
        currency = 'KZT',
        customerEmail,
        customerPhone,
        secretKey
    } = paymentData;

    try {
        // Генерируем соль
        const pg_salt = generateSalt();

        // Формируем параметры запроса
        const params = {
            pg_merchant_id: merchantId,
            pg_amount: amount.toString(),
            pg_order_id: orderId,
            pg_description: description,
            pg_currency: currency,
            pg_salt: pg_salt
        };

        // Добавляем опциональные параметры
        if (customerEmail) params.pg_user_contact_email = customerEmail;
        if (customerPhone) params.pg_user_phone = customerPhone;
        if (FREEDOM_PAY_CONFIG.testMode) params.pg_testing_mode = '1';

        // Генерируем подпись
        params.pg_sig = generateSignature(params, secretKey, 'payment.php');

        console.log('💳 Creating Freedom Pay payment:', {
            orderId,
            amount,
            currency
        });

        // Отправляем запрос
        const response = await fetch(`${FREEDOM_PAY_CONFIG.apiUrl}/v1/payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(params)
        });

        const result = await response.json();

        if (result.pg_status === 'ok') {
            console.log('✅ Freedom Pay payment created:', result);
            return {
                success: true,
                paymentId: result.pg_payment_id,
                redirectUrl: result.pg_redirect_url,
                data: result
            };
        } else {
            console.error('❌ Freedom Pay payment failed:', result);
            return {
                success: false,
                error: result.pg_error_description || 'Payment creation failed',
                errorCode: result.pg_error_code
            };
        }
    } catch (error) {
        console.error('❌ Freedom Pay API error:', error);
        throw error;
    }
};

/**
 * Проверка статуса платежа
 *
 * @param {string} paymentId - ID платежа в Freedom Pay
 * @returns {Promise}
 */
export const checkPaymentStatus = async (paymentId) => {
    try {
        // Этот запрос должен делаться через Firebase Functions
        // т.к. требует secret_key
        const response = await fetch(`/api/freedompay/status/${paymentId}`);
        const result = await response.json();

        return result;
    } catch (error) {
        console.error('❌ Error checking payment status:', error);
        throw error;
    }
};

/**
 * Загрузка Freedom Pay widget скрипта
 * ⚠️ Больше не используется - переключились на redirect integration
 *
 * @deprecated Используйте initFreedomPayWidget с redirect
 * @returns {Promise}
 */
export const loadFreedomPayScript = () => {
    // Больше не нужно загружать скрипт для redirect integration
    return Promise.resolve();
};

/**
 * Тестовые карты Freedom Pay
 */
export const TEST_CARDS = {
    success: {
        pan: '4444444444446666',
        cvv: '123',
        month: '12',
        year: '25',
        name: 'TEST CARD'
    },
    visa: {
        pan: '4111111111111111',
        cvv: '123',
        month: '12',
        year: '25',
        name: 'TEST VISA'
    },
    mastercard: {
        pan: '5555555555555557',
        cvv: '123',
        month: '12',
        year: '25',
        name: 'TEST MASTERCARD'
    }
};

export default {
    generateSignature,
    generateSalt,
    generateOrderId,
    initFreedomPayWidget,
    createPayment,
    checkPaymentStatus,
    loadFreedomPayScript,
    TEST_CARDS,
    config: FREEDOM_PAY_CONFIG
};
